import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import api from "@/api";

export default function Camera({
  model_type,
  model_id,
  onPhotoCapture,
  onVideoCapture,
  autoActivate = false,
  uploadUrl = "/attachments",
  uploadHeaders = {},
}) {
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [description, setDescription] = useState("");
  const debugMode =
    import.meta.env.VITE_DEBUG_MODE === "true" ||
    import.meta.env.VITE_DEBUG_MODE === true;
  // Upload function for image or video
  async function uploadMedia({ dataUrl, blob, type }) {
    if (!model_type || !model_id) {
      setError("Missing model_type or model_id for upload");
      return null;
    }
    let formData = new FormData();
    formData.append("model_type", model_type);
    formData.append("model_id", model_id);
    if (description && description.trim() !== "") {
      formData.append("description", description.trim());
    }
    if (type === "image") {
      // Convert dataUrl to Blob
      const res = await fetch(dataUrl);
      const imageBlob = await res.blob();
      formData.append("file", imageBlob, "photo.jpg");
    } else if (type === "video") {
      formData.append("file", blob, "video.mp4");
    }
    try {
      const response = await api.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...uploadHeaders,
        },
      });
      setError("");
      setUploadResult(response.data);
      setUploadError(null);
      if (debugMode) {
        console.log("Upload successful:", response.data);
      }
      return response.data;
    } catch (err) {
      const msg = err?.response?.data?.message || err.message;
      setError("Upload failed: " + msg);
      setUploadError(msg);
      setUploadResult(null);
      if (debugMode) {
        console.error("Upload error:", err);
      }
      return { error: msg };
    }
  }
  // Common resolutions (width x height)
  const RESOLUTIONS = [
    { label: "Maximum Resolution", width: 0, height: 0 }, // Special value for max capability
    { label: "Ultra HD (4K)", width: 3840, height: 2160 },
    { label: "Full HD (1080p)", width: 1920, height: 1080 },
    { label: "HD (720p)", width: 1280, height: 720 },
    { label: "VGA (480p)", width: 640, height: 480 },
    { label: "QVGA (320x240)", width: 320, height: 240 },
  ];
  const [selectedResolution, setSelectedResolution] = useState(() => {
    // Try to restore from session storage
    try {
      const storedResolution = sessionStorage.getItem("selectedResolution");
      if (storedResolution) {
        const parsed = JSON.parse(storedResolution);
        // Find the matching resolution in RESOLUTIONS array
        const matchingRes = RESOLUTIONS.find(
          (res) => res.label === parsed.label
        );
        return matchingRes || RESOLUTIONS[0];
      }
      return RESOLUTIONS[0];
    } catch (err) {
      console.warn("Could not access session storage for resolution:", err);
      return RESOLUTIONS[0];
    }
  });
  const [availableResolutions, setAvailableResolutions] = useState(RESOLUTIONS);
  const [stream, setStream] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(() => {
    // Try to restore from session storage
    try {
      const storedCameraId = sessionStorage.getItem("selectedCameraId");
      console.log(
        "Initializing camera component with stored camera ID:",
        storedCameraId
      );
      return storedCameraId || "";
    } catch (err) {
      console.warn("Could not access session storage for camera:", err);
      return "";
    }
  });
  const [error, setError] = useState("");
  const [isCapturing, setIsCapturing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [mode, setMode] = useState("photo"); // 'photo' or 'video'
  const [activated, setActivated] = useState(false); // Changed to false - manual activation by default
  const [isVisible, setIsVisible] = useState(false); // Track component visibility
  const [currentResolution, setCurrentResolution] = useState(null);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0); // Track current camera index
  const [dropdownOpen, setDropdownOpen] = useState(false); // For resolution dropdown
  const [isInitializing, setIsInitializing] = useState(false); // Track initialization state
  const [isGettingCameras, setIsGettingCameras] = useState(false); // Prevent multiple getCameras calls
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null); // Reference to the container element
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const initTimeoutRef = useRef(null); // For initialization timeout

  // Intersection Observer to detect when component is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isCurrentlyVisible = entry.isIntersecting;
        setIsVisible(isCurrentlyVisible);

        // Auto-activate camera when visible (if autoActivate is true)
        if (autoActivate && isCurrentlyVisible && !activated) {
          setActivated(true);
        }
        // Auto-deactivate camera when not visible (if autoActivate is true)
        else if (autoActivate && !isCurrentlyVisible && activated) {
          setActivated(false);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
        rootMargin: "50px", // Start loading 50px before the component enters viewport
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [autoActivate, activated]);

  // Get available cameras (only after activation and when visible)
  useEffect(() => {
    if (!activated || (!autoActivate && !isVisible)) return;

    // Only get cameras if we don't have any yet
    if (cameras.length === 0) {
      getCameras();
    }
  }, [activated, isVisible, autoActivate]); // Added isVisible and autoActivate to dependencies

  // Start camera stream when camera or resolution changes (only when visible)
  useEffect(() => {
    if (!activated || !selectedCamera || selectedCamera === "undefined") {
      console.log(
        "Not starting camera - activated:",
        activated,
        "selectedCamera:",
        selectedCamera
      );
      return;
    }

    // Don't start camera if component is not visible and autoActivate is false
    if (!autoActivate && !isVisible) {
      console.log(
        "Not starting camera - component not visible and autoActivate is false"
      );
      return;
    }

    console.log("Starting camera with deviceId:", selectedCamera);

    async function startCamera() {
      try {
        setError("");
        setIsInitializing(true);

        // Clear any existing timeout
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
        }

        // Set a timeout to prevent getting stuck
        initTimeoutRef.current = setTimeout(() => {
          setError("Camera initialization timed out. Please try again.");
          setIsInitializing(false);
        }, 10000); // 10 second timeout

        // Always stop previous stream before starting a new one
        if (stream) {
          console.log("Stopping previous stream");
          stream.getTracks().forEach((track) => track.stop());
          setStream(null);
          // Add a small delay to ensure the stream is fully stopped
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Clear video element
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.poster = "";
        }

        // Set up initial constraints
        let constraints = {
          video: {
            deviceId: { exact: selectedCamera },
          },
          audio: mode === "video", // Always capture audio for video mode
        };

        // If resolution is specified (not the "Maximum Resolution" option)
        if (selectedResolution.width > 0 && selectedResolution.height > 0) {
          constraints.video.width = { ideal: selectedResolution.width };
          constraints.video.height = { ideal: selectedResolution.height };
        }

        // Add flash if enabled
        if (flashEnabled) {
          constraints.video.torch = true;
        }

        console.log(
          "Starting camera with constraints:",
          JSON.stringify(constraints)
        );

        // Get the stream with initial constraints (including audio for video mode)
        const newStream = await navigator.mediaDevices.getUserMedia(
          constraints
        );
        console.log(
          "Camera stream obtained successfully with audio:",
          newStream.getAudioTracks().length > 0
        );
        console.log("Video tracks:", newStream.getVideoTracks().length);
        console.log("Audio tracks:", newStream.getAudioTracks().length);

        // Clear timeout on success
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
          initTimeoutRef.current = null;
        }

        // Set the stream state first
        setStream(newStream);
        setIsInitializing(false);

        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          videoRef.current.poster = ""; // Clear any loading poster

          // Wait for metadata to load before trying to play
          const onLoadedMetadata = () => {
            if (videoRef.current) {
              videoRef.current.play().catch((playErr) => {
                console.warn("Video autoplay failed:", playErr);
              });
            }
          };

          if (videoRef.current.readyState >= 1) {
            // Metadata already loaded
            onLoadedMetadata();
          } else {
            // Wait for metadata to load
            videoRef.current.addEventListener(
              "loadedmetadata",
              onLoadedMetadata,
              { once: true }
            );
          }
        }

        // Get camera capabilities and current resolution
        if (newStream.getVideoTracks().length > 0) {
          const track = newStream.getVideoTracks()[0];
          const settings = track.getSettings();
          console.log("Camera settings:", settings);

          setCurrentResolution({
            width: settings.width,
            height: settings.height,
          });

          // Create a list of available resolutions based on camera capabilities
          const capabilities = track.getCapabilities();
          console.log("Camera capabilities:", capabilities);

          if (capabilities && capabilities.width && capabilities.height) {
            const availableRes = [RESOLUTIONS[0]]; // Always include "Maximum Resolution"

            // Add other resolutions that are within camera capabilities
            for (let i = 1; i < RESOLUTIONS.length; i++) {
              const res = RESOLUTIONS[i];
              if (
                res.width <= capabilities.width.max &&
                res.height <= capabilities.height.max
              ) {
                availableRes.push(res);
              }
            }

            setAvailableResolutions(availableRes);
          }

          // Apply flash/torch settings if supported
          if (flashEnabled && capabilities && capabilities.torch) {
            await track.applyConstraints({
              advanced: [{ torch: true }],
            });
          }
        }
      } catch (err) {
        console.error("Camera access error:", err);
        setError("Could not access camera: " + err.message);
        setIsInitializing(false);

        // Clear timeout on error
        if (initTimeoutRef.current) {
          clearTimeout(initTimeoutRef.current);
          initTimeoutRef.current = null;
        }

        // If this was a camera switch that failed, try to fall back to the first camera
        if (cameras.length > 0 && selectedCamera !== cameras[0].deviceId) {
          console.log("Attempting fallback to first camera");
          setCurrentCameraIndex(0);
          setSelectedCamera(cameras[0].deviceId);
        }
      }
    }

    startCamera();

    // Clean up previous stream on dependency change
    return () => {
      if (stream) {
        console.log("Cleanup: stopping stream");
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [
    selectedCamera,
    selectedResolution,
    flashEnabled,
    mode,
    activated,
    isVisible,
    autoActivate,
  ]);

  // Detect browser support and auto-start camera on initial load
  useEffect(() => {
    // Check for required browser features
    const checkBrowserSupport = () => {
      if (!navigator.mediaDevices) {
        setError("Media Devices API not supported in this browser");
        return false;
      }

      if (!navigator.mediaDevices.getUserMedia) {
        setError("Camera access not supported in this browser");
        return false;
      }

      if (!navigator.mediaDevices.enumerateDevices) {
        console.warn(
          "This browser does not support device enumeration, limited camera functionality will be available"
        );
        // We can still work with getUserMedia even if enumeration isn't supported
      }

      // Check if this is a secure context (HTTPS or localhost)
      if (window.isSecureContext === false) {
        setError("Camera access requires a secure connection (HTTPS)");
        return false;
      }

      return true;
    };

    const isSupported = checkBrowserSupport();
    console.log("Camera API supported:", isSupported);

    // Auto-start camera if supported and if component should be auto-activated
    if (
      isSupported &&
      activated &&
      cameras.length === 0 &&
      (autoActivate || isVisible)
    ) {
      getCameras();
    }

    // Set up listeners for device changes (e.g., plugging in a webcam)
    if (isSupported && navigator.mediaDevices.addEventListener) {
      const handleDeviceChange = () => {
        console.log("Media devices changed");
        // If camera is activated, refresh the camera list only if we don't already have cameras
        if (activated && !isGettingCameras && (autoActivate || isVisible)) {
          // Reset cameras array to force a refresh
          setCameras([]);
          setSelectedCamera("");
          getCameras();
        }
      };

      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange
      );

      return () => {
        // Cleanup on component unmount
        navigator.mediaDevices.removeEventListener(
          "devicechange",
          handleDeviceChange
        );
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      };
    }

    return () => {
      // Cleanup on component unmount when mediaDevices API isn't fully supported
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []); // Only run once on mount

  // Helper function to save selected camera to session storage
  function saveSelectedCamera(deviceId) {
    try {
      if (deviceId && deviceId !== "undefined" && deviceId.trim() !== "") {
        const previousValue = sessionStorage.getItem("selectedCameraId");
        sessionStorage.setItem("selectedCameraId", deviceId);
        console.log("Camera preference updated in session storage:");
        console.log("  Previous:", previousValue);
        console.log("  Current:", deviceId);

        // Find camera name for better logging
        const camera = cameras.find((cam) => cam.deviceId === deviceId);
        if (camera) {
          console.log("  Camera name:", camera.displayLabel);
        }
      } else {
        sessionStorage.removeItem("selectedCameraId");
        console.log(
          "Removed camera preference from session storage (invalid deviceId:",
          deviceId,
          ")"
        );
      }
    } catch (err) {
      console.warn("Could not save camera preference to session storage:", err);
    }
  }

  // Helper function to save selected resolution to session storage
  function saveSelectedResolution(resolution) {
    try {
      if (resolution) {
        sessionStorage.setItem(
          "selectedResolution",
          JSON.stringify(resolution)
        );
        console.log("Saved resolution to session storage:", resolution.label);
      } else {
        sessionStorage.removeItem("selectedResolution");
        console.log("Removed resolution from session storage");
      }
    } catch (err) {
      console.warn("Could not save resolution to session storage:", err);
    }
  }

  // Helper function to get cameras (moved outside the effect for reuse)
  async function getCameras() {
    console.log(
      "getCameras called, activated:",
      activated,
      "isGettingCameras:",
      isGettingCameras
    );

    // Prevent multiple simultaneous calls
    if (isGettingCameras) {
      console.log("getCameras already in progress, skipping");
      return;
    }

    setIsGettingCameras(true);

    try {
      // Request permission first
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        setError("Media devices API not supported in this browser");
        return;
      }

      // Try to get permissions by requesting a temporary stream
      try {
        console.log("Requesting camera permission...");
        const tempStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        // Stop the stream immediately after getting permissions
        tempStream.getTracks().forEach((track) => track.stop());
        console.log("Camera permission granted");

        // Also request audio permission for video recording
        if (mode === "video") {
          console.log("Requesting audio permission for video recording...");
          try {
            const audioStream = await navigator.mediaDevices.getUserMedia({
              audio: true,
              video: false,
            });
            audioStream.getTracks().forEach((track) => track.stop());
            console.log("Audio permission granted");
          } catch (audioErr) {
            console.warn(
              "Audio permission denied, video will be recorded without sound:",
              audioErr
            );
          }
        }

        // Now that we have permission, get the list of devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );

        console.log("Available cameras:", videoDevices);

        if (videoDevices.length > 0) {
          // Group cameras by label to handle duplicate entries (some browsers report front/back as separate devices)
          const uniqueCameras = [];
          const cameraLabels = new Set();

          videoDevices.forEach((camera) => {
            // Ensure we have a valid deviceId
            if (!camera.deviceId) {
              console.warn("Camera found without deviceId:", camera);
              return; // Skip cameras without deviceId
            }

            const label = camera.label || `Camera ${uniqueCameras.length + 1}`;
            // Add suffix to make labels unique if needed
            let uniqueLabel = label;
            let counter = 1;
            while (cameraLabels.has(uniqueLabel)) {
              uniqueLabel = `${label} (${counter++})`;
            }
            cameraLabels.add(uniqueLabel);

            uniqueCameras.push({
              deviceId: camera.deviceId,
              groupId: camera.groupId,
              kind: camera.kind,
              label: camera.label,
              displayLabel: uniqueLabel,
              isFront:
                label.toLowerCase().includes("front") ||
                label.toLowerCase().includes("user"),
            });
          });

          console.log(
            "Processed cameras with deviceIds:",
            uniqueCameras.map((cam) => ({
              label: cam.displayLabel,
              deviceId: cam.deviceId,
            }))
          );
          setCameras(uniqueCameras);

          // Always set selectedCamera if we have cameras available
          if (uniqueCameras.length > 0) {
            // Validate that all cameras have deviceId
            const validCameras = uniqueCameras.filter(
              (camera) => camera.deviceId
            );
            if (validCameras.length === 0) {
              console.error("No cameras with valid deviceId found");
              setError("No valid cameras detected");
              return;
            }

            console.log("Valid cameras found:", validCameras.length);

            // Try to restore the previously selected camera from session storage
            const storedCameraId = selectedCamera; // This already contains the value from session storage
            const storedCamera = storedCameraId
              ? validCameras.find(
                (camera) => camera.deviceId === storedCameraId
              )
              : null;

            if (storedCamera && storedCamera.deviceId) {
              // Found the stored camera, use it
              console.log(
                "Restoring stored camera:",
                storedCamera.displayLabel,
                "deviceId:",
                storedCamera.deviceId
              );
              setSelectedCamera(storedCamera.deviceId);
              setCurrentCameraIndex(validCameras.indexOf(storedCamera));
            } else if (
              !selectedCamera ||
              !validCameras.find((camera) => camera.deviceId === selectedCamera)
            ) {
              // No stored camera or stored camera not available, select default
              const frontCamera = validCameras.find((camera) => camera.isFront);
              const defaultCamera = frontCamera || validCameras[0];
              if (defaultCamera && defaultCamera.deviceId) {
                console.log(
                  "Setting default camera:",
                  defaultCamera.displayLabel,
                  "deviceId:",
                  defaultCamera.deviceId
                );
                setSelectedCamera(defaultCamera.deviceId);
                setCurrentCameraIndex(validCameras.indexOf(defaultCamera));
                // Save the default selection
                saveSelectedCamera(defaultCamera.deviceId);
              } else {
                console.error("Default camera has no deviceId");
                setError("Camera device access error");
                return;
              }
            } else {
              // Update the current camera index based on existing selection
              const existingCameraIndex = validCameras.findIndex(
                (camera) => camera.deviceId === selectedCamera
              );
              if (existingCameraIndex !== -1) {
                setCurrentCameraIndex(existingCameraIndex);
                console.log(
                  "Keeping existing camera:",
                  validCameras[existingCameraIndex].displayLabel,
                  "deviceId:",
                  selectedCamera
                );
                // Ensure it's saved in session storage
                saveSelectedCamera(selectedCamera);
              }
            }

            // Also ensure the current resolution is saved if it hasn't been saved yet
            try {
              const storedResolution =
                sessionStorage.getItem("selectedResolution");
              if (!storedResolution) {
                // Save the current resolution as default
                saveSelectedResolution(selectedResolution);
              }
            } catch (err) {
              // Ignore storage errors, just ensure we have a working resolution
            }
          }
        } else {
          setError("No cameras found on this device");
        }
      } catch (permErr) {
        console.error("Permission error:", permErr);
        setError("Camera permission denied. Please enable camera access.");
        return;
      }
    } catch (err) {
      console.error("Camera enumeration error:", err);
      setError("Cannot enumerate cameras: " + err.message);
    } finally {
      setIsGettingCameras(false);
    }
  }

  // Function to switch to next camera
  function switchToNextCamera() {
    if (cameras.length <= 1) {
      console.log("Only one camera available, cannot switch");
      return; // No switching needed if only one camera
    }

    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    if (!nextCamera) {
      console.error("Next camera not found at index", nextIndex);
      return;
    }

    console.log(
      `Switching from camera ${currentCameraIndex} (${cameras[currentCameraIndex]?.displayLabel}) to ${nextIndex} (${nextCamera.displayLabel})`
    );
    console.log(
      "Previous stored camera:",
      sessionStorage.getItem("selectedCameraId")
    );

    // Clear any previous errors
    setError("");

    // Update the camera selection immediately - this will trigger the useEffect to restart the stream
    setCurrentCameraIndex(nextIndex);
    setSelectedCamera(nextCamera.deviceId);

    // Save the new selection to session storage
    saveSelectedCamera(nextCamera.deviceId);
    console.log(
      "New stored camera after switch:",
      sessionStorage.getItem("selectedCameraId")
    );
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
    };
  }, [stream]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownOpen && !event.target.closest(".resolution-dropdown")) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  async function capturePhoto() {
    // Extra logging for debugging
    console.log("capturePhoto called");
    if (!videoRef.current) {
      setError("Video element not available");
      console.error("Video element not available");
      return;
    }
    if (!canvasRef.current) {
      setError("Canvas element not available");
      console.error("Canvas element not available");
      return;
    }
    if (!stream) {
      setError("Video stream not available");
      console.error("Video stream not available");
      return;
    }
    if (!model_type || !model_id) {
      setError("Camera requires model_type and model_id");
      console.error("Camera requires model_type and model_id");
      return;
    }

    // Additional validation to ensure stream has video tracks
    if (stream.getVideoTracks().length === 0) {
      setError("No video track available in stream");
      console.error("No video track available in stream");
      return;
    }

    // Check if video is actually playing
    const video = videoRef.current;
    if (video.readyState < 2) {
      setError("Video not ready for capture");
      console.warn(
        "Video not ready for capture, readyState:",
        video.readyState
      );
      // Try to force a refresh
      video.load();
      return;
    }

    try {
      setIsCapturing(true);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Set canvas size to match the actual video stream resolution
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      const width = settings.width || video.videoWidth;
      const height = settings.height || video.videoHeight;
      console.log("Capturing photo at resolution:", width, "x", height);
      console.log(
        "Video element size:",
        video.videoWidth,
        "x",
        video.videoHeight
      );
      console.log("Track settings:", settings);

      // Ensure we have valid dimensions
      if (!width || !height) {
        setError("Could not determine video dimensions");
        setIsCapturing(false);
        console.error("Could not determine video dimensions", {
          width,
          height,
        });
        return;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw current video frame to canvas at full resolution
      ctx.drawImage(video, 0, 0, width, height);

      // Convert to base64 data URL (using JPEG format with high quality)
      const photoDataUrl = canvas.toDataURL("image/jpeg", 0.95);
      console.log("Photo captured, dataUrl length:", photoDataUrl.length);

      // Upload the image and call parent callback with API response
      const apiResult = await uploadMedia({
        dataUrl: photoDataUrl,
        type: "image",
      });
      if (onPhotoCapture) {
        onPhotoCapture(apiResult);
      }

      setTimeout(() => setIsCapturing(false), 300); // Brief flash effect
    } catch (err) {
      console.error("Error capturing photo:", err);
      setError("Failed to capture photo: " + err.message);
      setIsCapturing(false);
    }
  }

  function startVideoRecording() {
    if (!stream || isRecording) return;
    if (!model_type || !model_id) {
      setError("Camera requires model_type and model_id");
      console.error("Camera requires model_type and model_id");
      return;
    }

    // Additional validation to ensure stream has video tracks
    if (stream.getVideoTracks().length === 0) {
      setError("No video track available for recording");
      return;
    }

    // Check if video is actually playing
    if (videoRef.current && videoRef.current.readyState < 2) {
      setError("Video not ready for recording");
      return;
    }

    try {
      recordedChunksRef.current = [];

      console.log("Starting video recording with stream tracks:", {
        video: stream.getVideoTracks().length,
        audio: stream.getAudioTracks().length,
      });

      // Try different codec options for better compatibility and MP4 output
      let mediaRecorder;
      const supportedTypes = [
        "video/mp4;codecs=h264,aac", // H.264 video + AAC audio (MP4)
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2", // Alternative MP4 codec specification
        "video/mp4", // Generic MP4
        "video/webm;codecs=vp9,opus", // VP9 video + Opus audio (WebM fallback)
        "video/webm;codecs=vp8,opus", // VP8 video + Opus audio (WebM fallback)
        "video/webm", // Generic WebM fallback
      ];

      let selectedMimeType = null;
      for (const type of supportedTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          selectedMimeType = type;
          console.log("Using MIME type:", type);
          break;
        }
      }

      if (!selectedMimeType) {
        console.warn("No supported MIME types found, using default");
        mediaRecorder = new MediaRecorder(stream);
      } else {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: selectedMimeType,
        });
      }

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Determine the appropriate MIME type for the blob
        let blobType = "video/mp4";
        if (selectedMimeType) {
          if (selectedMimeType.includes("mp4")) {
            blobType = "video/mp4";
          } else if (selectedMimeType.includes("webm")) {
            blobType = "video/webm";
          }
        }

        const blob = new Blob(recordedChunksRef.current, { type: blobType });
        const videoUrl = URL.createObjectURL(blob);
        console.log(
          "Video recorded with type:",
          blobType,
          "Size:",
          blob.size,
          "bytes"
        );

        // Upload the video and call parent callback with API response
        const apiResult = await uploadMedia({ blob, type: "video" });
        if (onVideoCapture) {
          onVideoCapture(apiResult);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Video recording error:", err);
      setError("Video recording not supported: " + err.message);
    }
  }
  // Require model_type and model_id as props
  Camera.propTypes = {
    model_type: PropTypes.string.isRequired,
    model_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    onPhotoCapture: PropTypes.func,
    onVideoCapture: PropTypes.func,
    autoActivate: PropTypes.bool,
    uploadUrl: PropTypes.string,
    uploadHeaders: PropTypes.object,
  };

  function stopVideoRecording() {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  }

  async function toggleFlash() {
    if (!stream || stream.getVideoTracks().length === 0) return;

    const track = stream.getVideoTracks()[0];
    const capabilities = track.getCapabilities();

    if (capabilities && capabilities.torch) {
      try {
        await track.applyConstraints({
          advanced: [{ torch: !flashEnabled }],
        });
        setFlashEnabled(!flashEnabled);
      } catch (err) {
        setError("Flash control not supported");
      }
    } else {
      setError("Flash not available on this camera");
    }
  }

  function switchCamera(deviceId) {
    // This function is now replaced by switchToNextCamera, but keeping for compatibility
    const cameraIndex = cameras.findIndex(
      (camera) => camera.deviceId === deviceId
    );
    if (cameraIndex !== -1) {
      setCurrentCameraIndex(cameraIndex);
    }

    if (deviceId === selectedCamera) return; // Don't switch if it's the same camera

    console.log(`Switching camera from ${selectedCamera} to ${deviceId}`);

    // Clear any previous errors
    setError("");

    // Show a temporary loading indicator on the video element
    if (videoRef.current) {
      const ctx = document.createElement("canvas").getContext("2d");
      if (ctx) {
        ctx.canvas.width = 320;
        ctx.canvas.height = 240;
        ctx.fillStyle = "#000000";
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.font = "20px Arial";
        ctx.fillStyle = "#FFFFFF";
        ctx.textAlign = "center";
        ctx.fillText(
          "Switching camera...",
          ctx.canvas.width / 2,
          ctx.canvas.height / 2
        );
        videoRef.current.srcObject = null;
        videoRef.current.poster = ctx.canvas.toDataURL();
      }
    }

    // Update the camera selection - this will trigger the useEffect to restart the stream
    setSelectedCamera(deviceId);

    // Save the new selection to session storage
    saveSelectedCamera(deviceId);
  }

  function toggleCamera() {
    // This function is simplified - now just toggles on/off
    if (activated) {
      // Turn off camera
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      setStream(null);
      setActivated(false);
      setError("");
      setIsInitializing(false);
      // Don't clear selectedCamera when turning off, keep it for next activation
    } else {
      // Turn on camera
      setActivated(true);
      setError(""); // Clear any previous errors when activating
      setIsInitializing(false);

      // Check for browser support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera API not supported in this browser");
        setActivated(false);
        return;
      }

      // Request camera permission and start initialization only if we don't have cameras
      if (cameras.length === 0) {
        getCameras();
      }
    }
  }

  function formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow relative"
    >
      <div className="flex items-center gap-4 w-full">
        <h3 className="text-lg font-semibold flex-1">Camera</h3>

        {/* Resolution selector button */}
        <div className="relative inline-block resolution-dropdown">
          <button
            type="button"
            className={`px-3 py-1 rounded text-sm font-medium transition-colors inline-flex items-center gap-2 ${activated
                ? "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                : "bg-gray-100 text-gray-500"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            disabled={!activated}
            title="Select resolution"
          >
            <svg
              className="size-4 opacity-60"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909.47.47a.75.75 0 11-1.06 1.061L6.53 8.091a.75.75 0 00-1.06 0l-4.97 4.97zm14.5-5.81a.75.75 0 00-.75-.75H3.25a.75.75 0 00-.75.75v.69l4.97 4.97 1.97-1.97 2.22 2.22L14.5 9.06v-3.81z"
                clipRule="evenodd"
              />
            </svg>
            <span className="hidden sm:inline">
              {selectedResolution.label === "Maximum Resolution"
                ? "Auto"
                : selectedResolution.label.split(" ")[0]}
            </span>
            <svg
              className={`size-3 opacity-50 transition-transform ${dropdownOpen ? "rotate-180" : ""
                }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-lg shadow-xl dark:shadow-gray-900">
              <div className="divide-y divide-gray-100 rounded-lg bg-white ring-1 ring-black/5 dark:divide-gray-700 dark:bg-gray-800 dark:ring-gray-700">
                <div className="space-y-1 p-2">
                  {availableResolutions.map((res, index) => (
                    <button
                      key={res.label}
                      type="button"
                      className={`group flex items-center justify-between gap-2 rounded-lg border border-transparent px-3 py-2 text-sm font-medium w-full text-left transition-colors ${selectedResolution.label === res.label
                          ? "bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-700/75 dark:hover:text-white"
                        }`}
                      onClick={() => {
                        setSelectedResolution(res);
                        setDropdownOpen(false);
                        // Save the resolution selection to session storage
                        saveSelectedResolution(res);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className="hi-mini hi-photo inline-block size-4 flex-none opacity-40 group-hover:opacity-60"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909.47.47a.75.75 0 11-1.06 1.061L6.53 8.091a.75.75 0 00-1.06 0l-4.97 4.97zm14.5-5.81a.75.75 0 00-.75-.75H3.25a.75.75 0 00-.75.75v.69l4.97 4.97 1.97-1.97 2.22 2.22L14.5 9.06v-3.81z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div className="flex flex-col">
                          <span className="font-medium">{res.label}</span>
                          {res.width > 0 && res.height > 0 && (
                            <span className="text-xs opacity-75">
                              {res.width}×{res.height}
                            </span>
                          )}
                        </div>
                      </div>
                      {selectedResolution.label === res.label && (
                        <svg
                          className="hi-mini hi-check inline-block size-4 text-blue-600 dark:text-blue-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Camera switch button - only show if multiple cameras are available */}
        {cameras.length > 1 && (
          <button
            onClick={switchToNextCamera}
            disabled={!activated || isInitializing}
            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${activated && !isInitializing
                ? "bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            title={
              isInitializing
                ? "Switching camera..."
                : `Switch to ${cameras[(currentCameraIndex + 1) % cameras.length]
                  ?.displayLabel || "next camera"
                }`
            }
          >
            {isInitializing ? "⏳ Switching..." : "🔄 Switch Camera"}
          </button>
        )}

        <button
          onClick={toggleCamera}
          className={`px-3 py-1 rounded text-sm font-medium ${activated
              ? "bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800"
              : "bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
            }`}
          title={`Camera is ${activated ? "ON" : "OFF"}${!autoActivate && !isVisible ? " (component not visible)" : ""
            }`}
        >
          {activated ? "📷 ON" : "📷 OFF"}
          {!autoActivate && !isVisible && (
            <span className="ml-1 text-xs opacity-75">(hidden)</span>
          )}
        </button>
      </div>

      {error && (
        <div className="text-red-600 dark:text-red-400 text-sm text-center">
          {error}
        </div>
      )}

      {/* Show current camera info */}
      {activated && cameras.length > 0 && (
        <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
          {cameras[currentCameraIndex]?.isFront ? "🤳" : "📷"}{" "}
          {cameras[currentCameraIndex]?.displayLabel || "Camera"}
          {cameras.length > 1 && (
            <span className="ml-2 text-xs">
              ({currentCameraIndex + 1} of {cameras.length})
            </span>
          )}
        </div>
      )}

      {/* Mode selector */}
      <div className="flex flex-col items-center gap-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Mode
        </label>
        <label className="group relative inline-flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <span
              className={
                mode === "photo" ? "text-blue-600 dark:text-blue-400" : ""
              }
            >
              📷
            </span>
            <span
              className={
                mode === "photo" ? "text-blue-600 dark:text-blue-400" : ""
              }
            >
              Photo
            </span>
          </div>
          <input
            type="checkbox"
            className="peer sr-only"
            checked={mode === "video"}
            onChange={(e) => setMode(e.target.checked ? "video" : "photo")}
            disabled={!activated}
          />
          <span className="relative h-7 w-12 flex-none rounded-full bg-gray-300 transition-all duration-150 ease-out peer-checked:bg-blue-500 peer-focus:ring-3 peer-focus:ring-blue-500/50 peer-focus:ring-offset-2 peer-focus:ring-offset-white peer-disabled:cursor-not-allowed peer-disabled:opacity-50 before:absolute before:top-1 before:left-1 before:size-5 before:rounded-full before:bg-white before:transition-transform before:duration-150 before:ease-out before:content-[''] peer-checked:before:translate-x-full dark:bg-gray-700 dark:peer-checked:bg-blue-500 dark:peer-focus:ring-offset-gray-900">
            {/* Camera icon for photo mode (left side) */}
            <svg
              className={`absolute top-1.5 left-1.5 size-4 text-gray-500 transition-opacity duration-150 ${mode === "photo" ? "opacity-100" : "opacity-0"
                }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 9a3 3 0 100 6 3 3 0 000-6z" />
              <path
                fillRule="evenodd"
                d="M9 1.5a.75.75 0 01.75-.75h4.5a.75.75 0 01.75.75V3h1.5A2.25 2.25 0 0118.75 5.25v13.5A2.25 2.25 0 0116.5 21h-9A2.25 2.25 0 015.25 18.75V5.25A2.25 2.25 0 017.5 3H9V1.5zM7.5 4.5a.75.75 0 00-.75.75v13.5c0 .414.336.75.75.75h9a.75.75 0 00.75-.75V5.25a.75.75 0 00-.75-.75h-9z"
                clipRule="evenodd"
              />
            </svg>
            {/* Video camera icon for video mode (right side) */}
            <svg
              className={`absolute top-1.5 right-1.5 size-4 text-white transition-opacity duration-150 ${mode === "video" ? "opacity-100" : "opacity-0"
                }`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M4.5 4.5a3 3 0 00-3 3v9a3 3 0 003 3h8.25a3 3 0 003-3v-9a3 3 0 00-3-3H4.5zM19.94 5.25c.966 0 1.75.784 1.75 1.75v10c0 .966-.784 1.75-1.75 1.75-.192 0-.384-.032-.566-.093L16.5 17.19v-10.38l2.874-1.462c.182-.061.374-.093.566-.093z" />
            </svg>
          </span>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <span
              className={
                mode === "video" ? "text-blue-600 dark:text-blue-400" : ""
              }
            >
              🎥
            </span>
            <span
              className={
                mode === "video" ? "text-blue-600 dark:text-blue-400" : ""
              }
            >
              Video
            </span>
          </div>
        </label>
      </div>

      {/* Current resolution display */}
      {currentResolution && (
        <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Current: {currentResolution.width}×{currentResolution.height}
          {mode === "video" && stream && stream.getAudioTracks().length > 0 && (
            <span className="ml-2">🎤 Audio</span>
          )}
        </div>
      )}

      {/* Flash control */}
      <button
        onClick={toggleFlash}
        disabled={!activated}
        className={`px-3 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed ${flashEnabled
            ? "bg-yellow-500 text-white hover:bg-yellow-600"
            : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
      >
        ⚡ Flash {flashEnabled ? "ON" : "OFF"}
      </button>

      <div className="relative">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-80 h-60 bg-black rounded border-2 ${isCapturing || isRecording ? "border-red-500 dark:border-red-600" : "border-gray-300 dark:border-gray-600"
            } ${!activated ? "opacity-50" : ""}`}
        />
        {!activated && (
          <div className="absolute inset-0 bg-gray-500 dark:bg-gray-700 opacity-75 rounded flex items-center justify-center">
            <span className="text-white text-lg font-semibold">Camera Off</span>
          </div>
        )}
        {isInitializing && (
          <div className="absolute inset-0 bg-black bg-opacity-75 rounded flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
              <span className="text-sm">Switching camera...</span>
            </div>
          </div>
        )}
        {isCapturing && (
          <div className="absolute inset-0 bg-white opacity-50 rounded"></div>
        )}
        {isRecording && (
          <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 rounded text-sm flex items-center gap-1">
            🔴 REC {formatTime(recordingTime)}
            {stream && stream.getAudioTracks().length > 0 && (
              <span className="text-xs">🎤</span>
            )}
          </div>
        )}
      </div>

      {/* Control buttons */}
      <div className="flex gap-2">
        {mode === "photo" ? (
          <button
            onClick={capturePhoto}
            disabled={!stream || error || isRecording || !activated}
            className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white font-semibold px-6 py-2 rounded-full shadow disabled:cursor-not-allowed"
          >
            📸 Capture Photo
          </button>
        ) : (
          <button
            onClick={isRecording ? stopVideoRecording : startVideoRecording}
            disabled={!stream || error || !activated}
            className={`font-semibold px-6 py-2 rounded-full shadow text-white disabled:cursor-not-allowed ${isRecording
                ? "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
              } disabled:bg-gray-400 dark:disabled:bg-gray-600`}
          >
            {isRecording ? "⏹️ Stop Recording" : "🎥 Start Recording"}
          </button>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />

      {/* Description textbox */}
      <div className="mt-2 flex flex-col items-center">
        <label
          htmlFor="camera-description"
          className="text-sm text-gray-700 dark:text-gray-300 mb-1"
        >
          Description (optional):
        </label>
        <textarea
          id="camera-description"
          className="w-72 p-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Add a description for this upload..."
        />
      </div>

      {/* Upload result and error UI (debug mode only) */}
      {debugMode && uploadResult && (
        <div className="text-green-600 dark:text-green-300 text-sm break-all mt-2">
          Upload successful!
          <br />
          Response: {JSON.stringify(uploadResult)}
        </div>
      )}
      {debugMode && uploadError && (
        <div className="text-red-600 dark:text-red-300 text-sm break-all mt-2">
          Error: {uploadError}
        </div>
      )}
    </div>
  );
}
